import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const relatedArticles = id ? getRelatedArticles(id, 3) : [];

  if (!article) {
    return (
      <>
        <SEO title="文章未找到" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该文章可能已被删除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '瓜瓜基地',
    },
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.summary}
        keywords={`瓜瓜基地,${article.tags.join(',')},${article.title}`}
        canonicalUrl={`https://guaguabase.com/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.summary}
        ogImage={article.coverImage}
        ogType="article"
        articlePublishedTime={article.publishDate}
        articleAuthor={article.author}
        articleTags={article.tags}
      />
      <JsonLd data={articleSchema} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18 bg-background-50">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '知识分享', href: '/category?tab=articles' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-8 md:py-12">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-6 md:mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full bg-accent-50 text-accent-700 font-medium">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2 py-1 rounded-full bg-background-100 text-foreground-500">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}阅读
                </span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8 md:mb-10 bg-background-100">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
                prose-h4:font-heading prose-h4:text-xl prose-h4:text-foreground-900 prose-h4:mt-8 prose-h4:mb-4
                prose-p:text-foreground-700 prose-p:leading-loose prose-p:mb-4
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h3 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6 md:mb-8">相关推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="group bg-background-50 rounded-2xl border border-background-200 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-background-100">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-accent-50 text-accent-700">{ra.category}</span>
                        <span className="text-xs text-foreground-400">{ra.readTime}阅读</span>
                      </div>
                      <h4 className="font-heading text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {ra.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}